import { Checkbox, Form, Input, Row, Select, Button } from "antd";
import {
  DepartmentInfo,
  DepartmentRole,
  GroupInfo,
  MemberDetailInfoType,
} from "../type";
import { WorkOutline } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import styled from "@emotion/styled";
import { Avatar } from "Components";
import { getFullInfoInDepartment, getMemberStatusText } from "utils";
import { MemberStatus } from "constant/enum";
import { useEffect, useState } from "react";
import styles from "../../hr.module.css";
import { toast } from "react-toastify";
import { AvatarCropModal } from "./AvatarCropModal";
import { ResponseDataType, uploadFileRequest, putRequest } from "api";
import { useAppContext } from "Context/AppContext"; // Thêm import AppContext
import { DepartmentNamecode } from "constant/department"; // Thêm import DepartmentNamecode

interface MemberDetailInfoPropType {
  memberInfo: MemberDetailInfoType;
  departmentList: DepartmentInfo[];
  groupList: GroupInfo[];
  isEditing: boolean;
}

export const MemberDetailInfo = (props: MemberDetailInfoPropType) => {
  const {
    memberInfo,
    departmentList,
    groupList: orgGroupList,
    isEditing,
  } = props;
  const {
    id,
    email,
    name,
    phone,
    avatar,
    department,
    departmentNamecode,
    role,
    groupList,
    status,
  } = memberInfo;
  const { currentOrgInfo } = useAppContext(); // Lấy currentOrgInfo từ context
  const emailLocalStorage = localStorage.getItem("email");
  const [form] = Form.useForm();

  const [editAvatar, setEditAvatar] = useState<boolean>(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
  const [showCrop, setShowCrop] = useState(false);
  const [imageSrcForCrop, setImageSrcForCrop] = useState<string | null>(null);

  const handleUploadAvatar = async () => {
    if (!avatarFile) {
      toast.error("Chưa chọn avatar mới");
      return;
    }

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append("files", avatarFile);

    try {
      const uploadResponse: ResponseDataType<{
        success?: boolean;
        uploadedFiles?: { url: string }[];
        message?: string | string[];
      }> = await uploadFileRequest(`/files/upload-image?type=avatar`, formData);

      if (uploadResponse.code === 0 && uploadResponse.data?.uploadedFiles?.[0]?.url) {
        const newAvatarUrl = uploadResponse.data.uploadedFiles[0].url;

        const updateResponse = await putRequest(`/member/update-avatar`, {
          avatar: newAvatarUrl,
          memberId: id, // Thêm memberId để cập nhật avatar của thành viên đúng
        });

        if (updateResponse.code === 0) {
          toast.success("Cập nhật avatar thành công");
          setPreviewAvatarUrl(null);
          setAvatarFile(null);
          form.setFieldsValue({ avatar: "" });
          // window.location.reload(); // Tải lại trang, bỏ cache
        } else {
          toast.error(
            typeof updateResponse.message === "string"
              ? updateResponse.message
              : updateResponse.message?.join(", ") || "Lỗi khi cập nhật avatar"
          );
        }
      } else {
        toast.error(
          typeof uploadResponse.message === "string"
            ? uploadResponse.message
            : uploadResponse.message?.join(", ") || "Lỗi khi tải file"
        );
      }
    } catch (error) {
      console.error("Lỗi khi cập nhật avatar:", error);
      toast.error("Lỗi không xác định, vui lòng thử lại");
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    // Cho phép chỉnh sửa avatar nếu người dùng là CEO, HCNS hoặc là chính thành viên đó
    const canEditAvatar =
      [DepartmentNamecode.CEO, DepartmentNamecode.HCNS].includes(
        currentOrgInfo?.memberDepartmentCode as DepartmentNamecode
      ) || email === emailLocalStorage;
    setEditAvatar(canEditAvatar);
  }, [email, emailLocalStorage, currentOrgInfo?.memberDepartmentCode]);

  if (id === "")
    return (
      <Row justify="center">
        <CircularProgress size={50} sx={{ color: "black" }} />
      </Row>
    );

  return (
    <div style={{ maxWidth: "600px" }}>
      <Row align="middle">
        <div className={styles.avatar_wrapper}>
          <Avatar
            src={previewAvatarUrl || avatar}
            className={styles.avatar_img}
          />
          {editAvatar && (
            <div
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/jpeg,image/png";
                input.onchange = (e: Event) => {
                  const target = e.target as HTMLInputElement;
                  const file = target.files?.[0];
                  if (file) {
                    if (file.size > 5000000) {
                      toast.error(`File ${file.name} vượt quá 5MB`);
                      return;
                    }
                    const imageUrl = URL.createObjectURL(file);
                    setImageSrcForCrop(imageUrl);
                    setShowCrop(true);
                  }
                };
                input.click();
              }}
              className={styles.edit_text}
            >
              Edit
            </div>
          )}
          {showCrop && imageSrcForCrop && (
            <AvatarCropModal
              imageSrc={imageSrcForCrop}
              onCropDone={(croppedFile: File) => {
                setAvatarFile(croppedFile);
                setPreviewAvatarUrl(URL.createObjectURL(croppedFile));
                setShowCrop(false);
                form.setFieldsValue({ avatar: "" });
              }}
              onCancel={() => {
                setShowCrop(false);
                setImageSrcForCrop(null);
              }}
            />
          )}
        </div>

        <div>
          {isEditing ? (
            <Form.Item
              name="name"
              rules={[{ required: true, message: "Cần nhập tên!" }]}
            >
              <Input style={formItemFontStyle} />
            </Form.Item>
          ) : (
            <div style={{ fontSize: 14, fontWeight: 700 }}>{name}</div>
          )}
          <Row style={{ fontSize: 12, margin: "5px 0" }}>
            <div style={{ fontWeight: 700 }}>Email:</div> 
            <div style={{ color: "black", fontWeight: 500 }}>{email}</div>
          </Row>
          {isEditing ? (
            <Form.Item name="phone">
              <Input style={formItemFontStyle} />
            </Form.Item>
          ) : (
            <Row style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 700 }}>Phone:</div> 
              <div style={{ color: "black", fontWeight: 500 }}>
                {phone || ""}
              </div>
            </Row>
          )}
        </div>
      </Row>
      {avatarFile && (
        <div style={{ marginTop: 20 }}>
          Đã chọn: {avatarFile.name}{" "}
          <Button
            size="small"
            style={{ marginLeft: 5 }}
            onClick={() => {
              setAvatarFile(null);
              setPreviewAvatarUrl(null);
              form.setFieldsValue({ avatar: "" });
            }}
          >
            Xóa
          </Button>
          <Button
            size="small"
            style={{ marginLeft: 10 }}
            onClick={handleUploadAvatar}
            loading={uploadingAvatar}
          >
            Cập nhật avatar
          </Button>
        </div>
      )}
      <RowWrapper style={{ marginTop: 30 }}>
        <WorkIcon />
        <InfoFieldWrapper>
          <InfoFieldLabel>Vị trí công việc</InfoFieldLabel>
          {isEditing ? (
            <div style={{ width: "100%", maxWidth: 300 }}>
              <Form.Item name="department">
                <Select
                  optionFilterProp="label"
                  showSearch={true}
                  style={{ marginBottom: 0 }}
                  allowClear
                  labelRender={label => (
                    <div style={formItemFontStyle}>{label.label}</div>
                  )}
                  options={departmentList.map(d => ({
                    value: d.id,
                    label: d.name,
                  }))}
                  optionRender={option => (
                    <div style={formItemFontStyle}>{option.label}</div>
                  )}
                />
              </Form.Item>
              <Form.Item
                name="role"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value && !getFieldValue("department")) {
                        return Promise.reject("Chưa chọn phòng ban");
                      }

                      if (getFieldValue("department") && value === undefined) {
                        return Promise.reject(
                          "Cần chọn vai trò trong phòng ban"
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Select
                  style={{ marginBottom: 0 }}
                  allowClear
                  options={[
                    { label: "Trưởng phòng", value: DepartmentRole.MANAGER },
                    { label: "Nhân viên", value: DepartmentRole.STAFF },
                  ]}
                  labelRender={label => (
                    <div style={formItemFontStyle}>{label.label}</div>
                  )}
                  optionRender={option => (
                    <div style={formItemFontStyle}>{option.label}</div>
                  )}
                />
              </Form.Item>
            </div>
          ) : (
            <InfoFieldContent>
              {department
                ? getFullInfoInDepartment({
                    isOldInfo: status === MemberStatus.DEACTIVATE,
                    isManager: role === DepartmentRole.MANAGER,
                    department:
                      status === MemberStatus.DEACTIVATE
                        ? {
                            name: department,
                            namecode: departmentNamecode || "",
                          }
                        : departmentList.find(d => d.id === department),
                  })
                : "Chưa tham gia"}
            </InfoFieldContent>
          )}
        </InfoFieldWrapper>
      </RowWrapper>
      <RowWrapper>
        <WorkIcon />
        <InfoFieldWrapper>
          <InfoFieldLabel>Nơi làm việc</InfoFieldLabel>
          {isEditing ? (
            <Form.Item name="groups">
              <Checkbox.Group
                style={formItemFontStyle}
                options={orgGroupList.map(g => ({
                  value: g.id,
                  label: g.name,
                }))}
              />
            </Form.Item>
          ) : groupList && groupList.length ? (
            groupList.map((group, index) => (
              <InfoFieldContent key={index}>
                {status === MemberStatus.DEACTIVATE
                  ? group
                  : orgGroupList.find(g => g.id === group)?.name}
              </InfoFieldContent>
            ))
          ) : (
            <InfoFieldContent>Chưa tham gia</InfoFieldContent>
          )}
        </InfoFieldWrapper>
      </RowWrapper>
      <RowWrapper>
        <WorkIcon />
        <InfoFieldWrapper>
          <InfoFieldLabel>Trạng thái</InfoFieldLabel>
          <InfoFieldContent>{getMemberStatusText(status)}</InfoFieldContent>
        </InfoFieldWrapper>
      </RowWrapper>
    </div>
  );
};

const formItemFontStyle = {
  fontWeight: 700,
  fontSize: 20,
};

const RowWrapper = styled(Row)`
  margin: 0 0 30px 0px;
`;

const WorkIcon = styled(WorkOutline)`
  margin-right: 35px;
  font-size: 24px;
  color: #445bcd;
`;

const InfoFieldWrapper = styled.div`
  border-bottom: 1px solid #0005;
  flex-grow: 1;
  margin-right: 50px;
`;

const InfoFieldLabel = styled.div`
  font-size: 12px;
  color: #959595;
  margin-bottom: 10px;
`;

const InfoFieldContent = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 10px;
`;